import { useMutation } from '@apollo/client';
import { ChangeEvent, FC, useState } from 'react';
import Button from '../../../../components/form/button';
import Field from '../../../../components/form/field';
import Input from '../../../../components/form/input';
import Modal from '../../../../components/modal/default';
import {
  IInvitePatientArgs,
  PATIENT_INVITE,
  UPDATE_PATIENT_INFO,
  UpdatePatientInfoInput,
} from '../../../../graphql/mutation/patients';
import useToaster from '../../../../hooks/toast';
import { GraphQLResponse, IModalDefault, PatientView } from '../../../../types';
import { logger } from '../../../../utils/logger';
import { CountryCode, normalizePhoneNumber } from '../../../../utils/phone';
import styles from './remindPatientSignup.module.css';

interface IRemindPatientSignupProps extends IModalDefault {
  patient: PatientView;
}

// legacy fallback until LCX-965 backfill — splits `name` for old patients.
const splitLegacyName = (name?: string): { first: string; last: string } => {
  if (!name) return { first: '', last: '' };
  const trimmed = name.trim();
  const idx = trimmed.indexOf(' ');
  if (idx === -1) return { first: trimmed, last: '' };
  return { first: trimmed.slice(0, idx), last: trimmed.slice(idx + 1) };
};

const RemindPatientSignup: FC<IRemindPatientSignupProps> = props => {
  const { onClose, patient, ...modalProps } = props;
  const { signupCode, email: defaultEmail } = patient;
  const defaultPhoneMobile = patient.phoneMobile;

  const hasSplit = patient.nameFirst != null || patient.nameLast != null;
  const legacy = hasSplit ? null : splitLegacyName(patient.name);
  const defaultNameFirst = patient.nameFirst ?? legacy?.first ?? '';
  const defaultNameLast = patient.nameLast ?? legacy?.last ?? '';

  const [nameFirst, setNameFirst] = useState<string>(defaultNameFirst);
  const [nameLast, setNameLast] = useState<string>(defaultNameLast);
  const [email, setEmail] = useState<string>(defaultEmail || '');
  const [phoneMobile, setPhoneMobile] = useState<string>(defaultPhoneMobile || '');
  const { success, danger } = useToaster();

  const [updatePatientInfo, { loading: loadingUpdatePatient }] = useMutation<
    GraphQLResponse<'updatePatientInfo', PatientView>,
    { input: UpdatePatientInfoInput }
  >(UPDATE_PATIENT_INFO);
  const [invitePatient, { loading: loadingSendInvite }] = useMutation<
    GraphQLResponse<'invitePatientViaDynamicLink', IInvitePatientArgs>
  >(PATIENT_INVITE);

  const handleClose = () => {
    onClose();
  };

  const handleOnChangeInput =
    (type: 'nameFirst' | 'nameLast' | 'email' | 'phoneMobile') =>
    (e: ChangeEvent<HTMLInputElement>) => {
      if (type === 'nameFirst') setNameFirst(e.target.value);
      if (type === 'nameLast') setNameLast(e.target.value);
      if (type === 'email') setEmail(e.target.value);
    };

  const handleOnChangePhoneMobile = (countryCode: CountryCode, phoneNumber: string) => {
    setPhoneMobile(normalizePhoneNumber(countryCode, phoneNumber));
  };

  const hasPhoneOrEmailValue = (): boolean => {
    if (!!phoneMobile || !!email) return true;
    return false;
  };

  const handleUpdatePatient = async (): Promise<boolean> => {
    // handle possible updates to patient data if it was missing
    const keysToUpdate = {
      ...(nameFirst && nameFirst !== defaultNameFirst && { nameFirst }),
      ...(nameLast !== defaultNameLast && { nameLast }),
      ...(email && email !== defaultEmail && { email }),
      ...(phoneMobile && phoneMobile !== defaultPhoneMobile && { phoneMobile }),
    };

    // if no updates, return
    if (Object.keys(keysToUpdate).length === 0) return hasPhoneOrEmailValue();

    // update patient, if error, return false since we can't send invite
    try {
      await updatePatientInfo({
        variables: {
          input: {
            signupCode,
            ...keysToUpdate,
          },
        },
      });
      return true;
    } catch (err: any) {
      danger(`Problem sending invitation. Error: ${err.message}`);
      // eslint-disable-next-line no-param-reassign
      err.message = `Problem sending invitation. ${err.message}`;
      logger.error(err);
      return false;
    }
  };

  const handleSendInvite = async () => {
    // possibly update patient details
    const canSendInvite = await handleUpdatePatient();

    // error handled by called function, cannot proceed
    if (!canSendInvite) {
      onClose();
      return;
    }

    // send invite
    await invitePatient({
      variables: {
        signupCode,
        overrideResend: true,
      },
    }).catch(err => {
      danger(`Problem sending invitation. Error: ${err.message}`);
      // eslint-disable-next-line no-param-reassign
      err.message = `Problem sending invitation. ${err.message}`;
      logger.error(err);
    });

    success('Invitation sent successfully');
    onClose();
  };

  return (
    <Modal
      {...modalProps}
      onClose={handleClose}
      title="Send Invitation to Patient"
      style={{
        content: {
          width: '80%',
          maxWidth: '950px',
        },
      }}>
      <div className={styles.modalWrapper}>
        <p className={styles.modalBodyText}>
          Send invitation to patient via SMS or email now? Update patient record with any missing
          details.
        </p>
        <div className={styles.row}>
          <div className={styles.rowItem}>
            <Input.Field
              label="First Name"
              value={nameFirst}
              placeholder="First"
              disabled={!!defaultNameFirst}
              onChange={handleOnChangeInput('nameFirst')}
              className={styles.inputDetails}
            />
          </div>
          <div className={styles.rowItem}>
            <Input.Field
              label="Last Name"
              value={nameLast}
              placeholder="Last"
              disabled={!!defaultNameLast}
              onChange={handleOnChangeInput('nameLast')}
              className={styles.inputDetails}
            />
          </div>
          <div className={styles.rowItem}>
            <Input.Field
              label="Email"
              value={email}
              placeholder="user@domain.com"
              disabled={!!defaultEmail}
              onChange={handleOnChangeInput('email')}
              className={styles.inputDetails}
            />
          </div>
          <div className={styles.rowItem}>
            <Input.Phone
              label="Mobile Phone"
              defaultPhone={phoneMobile}
              placeholder="07123456789"
              disabled={!!defaultPhoneMobile}
              onChange={handleOnChangePhoneMobile}
            />
          </div>
        </div>

        {!hasPhoneOrEmailValue() && (
          <Field className={styles.error}>Phone number and/or Email is required</Field>
        )}

        <div className="buttons">
          <Button onClick={handleClose} disabled={loadingUpdatePatient || loadingSendInvite}>
            Cancel
          </Button>

          <Button
            onClick={handleSendInvite}
            variant="dark"
            isLoading={loadingUpdatePatient || loadingSendInvite}
            disabled={loadingUpdatePatient || loadingSendInvite || !hasPhoneOrEmailValue()}>
            Invite
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default RemindPatientSignup;
